// Reads the runtime config injected by /config/config.js (window.VIEWER_CONFIG).
// Everything that needs configuration goes through this module so the rest of
// the app never touches `window` directly.

export type CaseGrouping = 'patient' | 'study';

/**
 * How this deployment expects a person to prove who they are.
 *
 * A DISCRIMINATED UNION rather than one optional-field shape, because the two
 * modes need different things and `null` was already carrying too much meaning:
 * it said "no auth at all", which was true while the only alternative was OIDC.
 * Now the hub can issue its own sessions, and "there is a login, it just isn't
 * a redirect" needs to be sayable. Conflating them is how the app ends up
 * rendering itself for a caller who will 401 on every request.
 */
export type AuthConfig =
  /** The institution's own IdP: authorization code + PKCE, off to their domain. */
  | { mode: 'oidc'; authority: string; clientId: string }
  /**
   * This hub holds the credentials. Email and password, no redirect anywhere.
   *
   * NO `signup` flag. It used to live here, set per edition at deploy time —
   * which cannot represent an administrator closing registration this
   * afternoon, and would leave the sign-in screen offering a link that 403s.
   * The screen asks GET /auth/config instead, and the server is the only
   * authority on it.
   */
  | { mode: 'password' };

export interface RuntimeConfig {
  /**
   * How to sign in. NEVER null: every deployment authenticates now, so there is
   * no "boots straight in" shape left to represent.
   *
   * It used to be nullable, for AUTH_MODE=off. When that mode went away the
   * null stayed — and it was the DEFAULT, so a config.js with no `auth` key
   * (the local compose stack's, which never needed one) produced a viewer that
   * drew a worklist, asked for studies with no credential, and rendered the
   * resulting 401s as an empty list with no sign-in screen anywhere. The
   * least-secure reading of a missing value, silently: the same shape as
   * AUTH_MODE defaulting to 'off', one layer up.
   *
   * The session token ends up in a same-origin cookie either way, so
   * header-less requests — <img> thumbnails, EventSource — authenticate too.
   */
  auth: AuthConfig;
  /** Base URL of the DICOMweb API (QIDO/WADO/STOW), no trailing slash. */
  dicomWebRoot: string;
  /**
   * Base URL of the server's own API — everything that is not DICOMweb:
   * auth, workspaces, collections, uploads, shares, annotations — no trailing
   * slash. Defaults to the origin of dicomWebRoot plus /api, which is where
   * the server mounts it (server/src/http/mount.ts).
   */
  apiRoot: string;
  /** How the worklist groups studies into cases. */
  caseGrouping: CaseGrouping;
  /**
   * The synthetic base PixelSpacing (mm/px) the convert pipeline assigns to
   * images without real scanner metadata (WSI_BASE_PIXEL_SPACING_MM). When a
   * slide's base spacing matches this, the physical scale bar is suppressed and
   * magnification is shown as approximate, so we don't imply false precision.
   * Set to 0 to disable the guard.
   */
  syntheticPixelSpacingMm: number;
  /**
   * The PostHog project key, or undefined for no analytics at all.
   *
   * Set only in a community production deployment's config.js (cdk.json
   * `posthogKey`). Enterprise never gets one — it can carry PHI — and the app
   * checks the edition as well, so a key copied into the wrong config.js still
   * sends nothing. Not a secret: a project key can only send events.
   */
  posthogKey?: string;
  /** Where events go: PostHog's US endpoint, or this origin's /relay once it exists. */
  posthogHost: string;
  /**
   * A website owns `/` on this origin, and every path the app does not claim
   * (cdk.json `siteAtRoot`, infra/lib/cdn-routing.ts). The app then leaves `/`
   * to it: a guest there goes to the website rather than Explore, a signed-in
   * person to Collections, and Terms and Privacy are the website's pages.
   */
  website: boolean;
}

declare global {
  interface Window {
    VIEWER_CONFIG?: Partial<RuntimeConfig>;
  }
}

const DEFAULTS: RuntimeConfig = {
  // Password, not null: see RuntimeConfig.auth. OIDC cannot be a default — it
  // needs an authority and a client id that cannot be guessed — and password is
  // the mode that works with no further configuration.
  auth: { mode: 'password' },
  dicomWebRoot: 'http://localhost:8042/dicom-web',
  apiRoot: '',
  caseGrouping: 'patient',
  syntheticPixelSpacingMm: 0.00025,
  posthogHost: 'https://us.i.posthog.com',
  website: false,
};

/**
 * Accepts the older `{ authority, clientId }` shape, which had no `mode`.
 *
 * A deployed config.js is written by `cdk deploy` and a bind-mounted file is
 * edited by hand, so both outlive any single release of this bundle. Refusing
 * an older shape would mean a viewer that boots into "no auth" against a server
 * that demands one: a blank worklist and a console full of 401s, which reads as
 * a broken deployment rather than a stale config file.
 */
function normalizeAuth(raw: unknown): AuthConfig {
  if (!raw || typeof raw !== 'object') return { mode: 'password' };
  const a = raw as Partial<{ mode: string; authority: string; clientId: string }>;
  // A `signup` field in an older config is ignored rather than rejected: a
  // deployed config.js outlives any one bundle, and the server decides anyway.
  if (a.mode === 'password') return { mode: 'password' };
  if (a.authority && a.clientId) {
    return { mode: 'oidc', authority: a.authority, clientId: a.clientId };
  }
  // Unrecognised shape: fall back to the mode that needs no other setting,
  // never to "no authentication" — which is precisely what this function's own
  // docblock warns shows a blank worklist and a console full of 401s.
  return { mode: 'password' };
}

function deriveApiRoot(dicomWebRoot: string): string {
  try {
    return `${new URL(dicomWebRoot, window.location.origin).origin}/api`;
  } catch {
    return '';
  }
}

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

let cached: RuntimeConfig | null = null;

export function getConfig(): RuntimeConfig {
  if (cached) return cached;
  const injected = (typeof window !== 'undefined' && window.VIEWER_CONFIG) || {};
  cached = {
    auth: normalizeAuth(injected.auth ?? DEFAULTS.auth),
    dicomWebRoot: stripTrailingSlash(injected.dicomWebRoot ?? DEFAULTS.dicomWebRoot),
    apiRoot: stripTrailingSlash(
      injected.apiRoot ?? deriveApiRoot(injected.dicomWebRoot ?? DEFAULTS.dicomWebRoot),
    ),
    caseGrouping: injected.caseGrouping ?? DEFAULTS.caseGrouping,
    syntheticPixelSpacingMm: injected.syntheticPixelSpacingMm ?? DEFAULTS.syntheticPixelSpacingMm,
    posthogKey: injected.posthogKey || undefined,
    posthogHost: stripTrailingSlash(injected.posthogHost ?? DEFAULTS.posthogHost),
    website: injected.website === true,
  };
  return cached;
}
